import React from 'react'
import { useParentNavigation } from '@/hooks/use-parent-navigation'
import { ContextMenuButton } from '@/components/ui/context-menu-button'
import { cn } from '@/lib/utils'

interface ParentNavigationLinkProps {
  /** The route to navigate to in the parent app */
  route: string
  /** Whether to replace the current URL or push a new one */
  replaceUrl?: boolean
  /** Content to render inside the link */
  children: React.ReactNode
  /** Optional className for styling */
  className?: string
  /** Callback when navigation is attempted */
  onNavigate?: () => void
  /** Target for the link (similar to HTML target attribute) */
  target?: '_blank' | '_self' | '_parent' | '_top'
}

/**
 * A component that conditionally renders a navigation link/button based on iframe context.
 * Only renders when the app is in an iframe and can communicate with the parent.
 */
export function ParentNavigationLink({
  route,
  replaceUrl = false,
  children,
  className,
  onNavigate,
  target,
}: ParentNavigationLinkProps) {
  const { navigate, shouldRenderNavigationLinks } = useParentNavigation()

  const handleOpen = () => {
    // If target is _blank, open in new tab, otherwise use regular navigation
    if (target === '_blank') {
      navigate(route, { replaceUrl: false, openInNewTab: true })
    } else {
      navigate(route, { replaceUrl })
    }
    onNavigate?.()
  }

  const handleOpenInNewTab = () => {
    // For "open in new tab", use the new openInNewTab parameter
    navigate(route, { replaceUrl: false, openInNewTab: true })
    onNavigate?.()
  }

  // If in iframe context, render as clickable button with context menu
  if (shouldRenderNavigationLinks) {
    return (
      <ContextMenuButton
        className={className}
        onOpen={handleOpen}
        onOpenInNewTab={handleOpenInNewTab}
      >
        {children}
      </ContextMenuButton>
    )
  }

  // If not in iframe, just render the content as text
  return <span className={className}>{children}</span>
}

 